import { FileSharer } from "@byteowls/capacitor-filesharer";
import { Capacitor } from "@capacitor/core";
import { toErrorMessage } from "@clipboard-health/util-ts";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { isAndroidPlatform } from "@src/appV2/lib";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation } from "@tanstack/react-query";

import { APP_V2_APP_EVENTS, logEvent } from "../../../lib/analytics";
import { blobToBase64 } from "../../../lib/files/utils";
import { useToast } from "../../../lib/Notifications";
import { convertImageUrlToBase64 } from "./utils/convertImageUrlToBase64";
import { createIndependentContractorBadgePdf } from "./utils/createBadgePdf";
import { parseWorkerName } from "./utils/parseWorkerName";

async function sharePdf(pdfBlob: Blob, workerId: string, name: string, qualification: string) {
  if (Capacitor.getPlatform() === "web") {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    const url = URL.createObjectURL(pdfBlob);
    const newWindow = window.open(url, "_blank");

    if (newWindow) {
      newWindow.focus();
      logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SHARED, {
        workerId,
        qualification,
      });
      return;
    }

    throw new Error("Failed to open badge PDF in a new tab. Please check your browser settings.");
  }

  // Convert Blob to base64 for native sharing
  const base64DataUrl = await blobToBase64(pdfBlob);
  const base64Data = base64DataUrl.split(",")[1]; // Remove the data URL prefix
  const fileName = `${name.replace(/\s+/g, "_")}_contractor_badge.pdf`;

  try {
    await FileSharer.share({
      filename: fileName,
      base64Data,
      contentType: "application/pdf",
    });
  } catch (error: unknown) {
    // Suppress "USER_CANCELLED" error on Android as it's being thrown even when the user shares successfully
    if (!(isAndroidPlatform() && toErrorMessage(error) === "USER_CANCELLED")) {
      throw error;
    }
  }

  logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SHARED, {
    workerId,
    qualification,
  });
}

// @deprecated This code will be deleted in a future PR. It is replaced by the new badge generation flow.
export function useGenerateIndependentContractorBadge() {
  const { showErrorToast } = useToast();

  // We don't want to fetch these when the component mounts
  const worker = useDefinedWorker();

  const { userId: workerId, name, firstName, lastName } = worker;

  const { data: profilePicture } = useWorkerProfilePicture(workerId);

  return useMutation<void, Error, { licenseNumber: string | undefined; qualification: string }>({
    mutationFn: async ({ licenseNumber, qualification }) => {
      if (!name) {
        throw new Error("This worker doesn't have a name.");
      }

      if (!qualification) {
        throw new Error("This worker doesn't have a qualification.");
      }

      if (!profilePicture?.distribution_url) {
        throw new Error("This worker doesn't have a profile picture.");
      }

      const profilePictureBase64 = await convertImageUrlToBase64(profilePicture.distribution_url);

      const pdf = await createIndependentContractorBadgePdf({
        details: {
          ...parseWorkerName({ name, firstName, lastName }),
          qualification,
          licenseNumber,
          profilePictureBase64,
        },
      });

      await sharePdf(pdf.blob, workerId, name, qualification);
    },
    onError: (error) => {
      logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
        workerId,
      });

      showErrorToast(
        `Failed to generate independent contractor badge. ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    },
  });
}
